import {
    AfterViewInit,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    forwardRef,
    inject,
    input,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DsListItemComponent } from './ds-list-item/ds-list-item.component';
import {
    DsListItemDescriptionDirective,
    DsListItemDisabledDirective,
    DsListItemDividerDirective,
    DsListItemPrefixDirective,
    DsListItemSuffixDirective,
    DsListItemTitleDirective,
} from './ds-list-item/ds-list-item.directives';
import { Subject, takeUntil } from 'rxjs';
import { A11yModule } from '@angular/cdk/a11y';
import { DS_LIST_COMPONENT, IDsListComponent } from './ds-list.interface';

/**
 * A versatile list component that supports single/multiple selection, keyboard navigation,
 * search highlighting, and various display modes.
 *
 * Features:
 * - Single and multiple selection modes
 * - Keyboard navigation (arrow keys or tab mode)
 * - Search term highlighting
 * - Configurable item sizes (sm, md, lg)
 * - Dynamic item management (view children, content children, or input options)
 * - Accessibility support (ARIA attributes, focus management)
 * - Integration with overlays and context menus
 *
 * Usage:
 * ```html
 * <ds-list [size]="'md'" [multiple]="false" (selectionChange)="onSelect($event)">
 *   <ds-list-item value="item1">Item 1</ds-list-item>
 *   <ds-list-item value="item2">Item 2</ds-list-item>
 * </ds-list>
 * ```
 */
@Component({
    selector: 'ds-list',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'DsList u-spacing-none-ma u-spacing-none-pa',
        role: 'list',
        '[class.DsList--unselect]': '!select()',
        '[attr.size]': 'size()',
        '[attr.multiple]': 'multiple()',
        '(focusin)': 'onFocusIn($event)',
        '(focusout)': 'onFocusOut($event)',
        '(keydown)': 'onKeydown($event)',
    },
    imports: [CommonModule, A11yModule],
    providers: [{ provide: DS_LIST_COMPONENT, useExisting: forwardRef(() => DsListComponent) }],
    templateUrl: './ds-list.component.html',
    styleUrl: './ds-list.component.scss'
})
export class DsListComponent implements OnChanges, OnDestroy, AfterViewInit, IDsListComponent {
    // ============================================================================
    // Injects
    // ============================================================================

    readonly #elRef = inject(ElementRef);
    readonly #renderer = inject(Renderer2);

    // ============================================================================
    // Inputs (Signals)
    // ============================================================================

    /**
     * Size of the list items.
     * This will set the size of the items that are displayed in the list.
     *
     * Possible values:
     * - 'sm' - Small size
     * - 'md' - Medium size (default)
     * - 'lg' - Large size
     * - null - Default size
     */
    size = input<'sm' | 'md' | 'lg' | null>('md');

    /**
     * The search term to filter the list items.
     * This will highlight the search term in the list items.
     */
    searchTerm = input<string | undefined | null>();

    /**
     * The list of options to be displayed in the list.
     * This can be a list of DsListItemComponent.
     * If the list is empty, the component will look for the options QueryList.
     */
    options = input<QueryList<DsListItemComponent>>();

    /**
     * Whether multiple selection is enabled. If true, users can select multiple items in the list.
     */
    multiple = input<boolean>();

    /**
     * The list of selected items in the list.
     * This can be a list of strings representing the selected items.
     */
    selectedItems = input<string[]>([]);

    /**
     * Whether selection is enabled on list items.
     */
    select = input<boolean>(true);

    /**
     * When true, list items are in tab order; when false, use arrow keys for navigation.
     */
    tabbable = input<boolean>(false);

    // ============================================================================
    // Outputs
    // ============================================================================

    /**
     * Emits when the selection changes.
     * For single selection: emits a string value.
     * For multiple selection: emits an array of string values.
     */
    @Output() selectionChange: EventEmitter<string | string[]> = new EventEmitter<string | string[]>();

    // ============================================================================
    // View/Content Children
    // ============================================================================

    /**
     * Query list of list items from the view (template).
     */
    @ViewChild(forwardRef(() => DsListItemComponent))
    listItems!: QueryList<DsListItemComponent>;

    /**
     * Query list of list items from content projection.
     * Used for dynamically rendered lists in overlays and context menus.
     */
    @ContentChildren(DsListItemComponent)
    dropdownItems!: QueryList<DsListItemComponent>;

    // ============================================================================
    // Public Properties
    // ============================================================================

    /**
     * The active query list of items being displayed.
     * Can come from options, dropdownItems, or listItems.
     */
    itemQueryList!: QueryList<DsListItemComponent> | undefined;

    /**
     * Index of the currently focused item in the list.
     */
    dsListFocusIndex = 0;

    /**
     * Value of the list item currently in focus.
     */
    listValueInFocus = '';

    /**
     * Reference to the currently targeted element.
     */
    targetElement!: HTMLElement;

    // ============================================================================
    // Private Properties
    // ============================================================================

    /** Timeout ID for focus management debouncing. */
    #timeoutId!: ReturnType<typeof globalThis.setTimeout> | undefined;

    /** Subject for managing subscription cleanup. */
    readonly #unsubscribe$ = new Subject<void>();

    /** Array to track output subscriptions for manual cleanup. */
    #subscriptions: Array<{ unsubscribe: () => void }> = [];

    /** Internal signal for tracking selected items. */
    readonly #selectedItems = signal<string[]>(this.selectedItems());

    /** Mutation observer for watching DOM changes. */
    #observer: MutationObserver | null = null;

    // ============================================================================
    // Lifecycle Hooks
    // ============================================================================

    /**
     * Lifecycle hook called after the component's view has been fully initialized.
     *
     * Performs the following initialization tasks:
     * - Subscribes to options QueryList changes
     * - Subscribes to content children (dropdownItems) changes
     * - Populates the list items
     * - Initializes search term listener
     * - Initializes selection change listeners
     * - Sets up mutation observer for DOM changes
     */
    ngAfterViewInit() {
        // Subscribe to changes in the options QueryList. This is necessary to update the list items when the options change.
        this.itemQueryList?.changes.pipe(takeUntil(this.#unsubscribe$)).subscribe(() => {
            this.#initSelectionChangeListener();
        });

        // Subscribe to content children changes (for dynamically rendered lists in overlays)
        // This handles the case where list items are added after the component is initialized (e.g., in context menus)
        this.dropdownItems.changes.pipe(takeUntil(this.#unsubscribe$)).subscribe(() => {
            this.#populateListItems();
            this.#initSelectionChangeListener();
        });

        this.#populateListItems();
        this.#initSearchListener();
        this.#initSelectionChangeListener();
        this.#initMutationObserver();
    }

    /**
     * Lifecycle hook called when the component is destroyed.
     *
     * Performs cleanup:
     * - Completes all RxJS subscriptions
     * - Disconnects mutation observer
     * - Clears any pending timeouts
     */
    ngOnDestroy() {
        this.#unsubscribe$.next();
        this.#unsubscribe$.complete();

        // Clean up output subscriptions
        this.#subscriptions.forEach((sub) => sub.unsubscribe());
        this.#subscriptions = [];

        if (this.#observer) {
            this.#observer.disconnect();
        }
        if (this.#timeoutId) {
            clearTimeout(this.#timeoutId);
        }
    }

    /**
     * Lifecycle hook called when any input property changes.
     *
     * Handles changes to:
     * - `selectedItems`: Updates internal signal and synchronizes child item selection states
     * - `searchTerm`: Re-populates list items and triggers search term highlighting
     *
     * @param changes - Object containing the changed properties and their previous/current values
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes['selectedItems']) {
            this.#selectedItems.set(this.selectedItems());
            // Note: Child components will automatically react to changes in the parent's
            // selectedItems via their isSelected() method, no manual update needed
        }

        if (changes['searchTerm']) {
            this.#populateListItems();
            this.#highlightSearchTerm(changes['searchTerm'].currentValue);
        }
    }

    // ============================================================================
    // Event Handlers (Host Listeners)
    // ============================================================================

    /**
     * Handles the focusin event on the list component.
     * Sets the target element and tracks the focused item's value and index.
     *
     * @param event - The focus event
     */
    onFocusIn(event: FocusEvent) {
        const targetElement = event.target as HTMLElement;

        this.targetElement = targetElement;

        const reflectValue = targetElement.getAttribute('ng-reflect-value');
        if (reflectValue) {
            this.listValueInFocus = reflectValue;
            if (this.dsListFocusIndex === 1) {
                this.focusItem(1);
            }
        }
    }

    /**
     * Handles the focusout event on the list component.
     * Resets the focus index after a brief delay if focus has left the component entirely.
     */
    onFocusOut() {
        if (this.#timeoutId) {
            clearTimeout(this.#timeoutId); // Clear any existing timeout
        }

        this.#timeoutId = globalThis.setTimeout(() => {
            if (!document.activeElement || !this.#elementIsInsideComponent(document.activeElement as HTMLElement)) {
                this.dsListFocusIndex = 0;
            }
        }, 0);
    }

    /**
     * Handles keyboard navigation and interaction within the list.
     * Supports arrow keys for navigation, Enter for selection, Escape to close overlays,
     * and Tab navigation when tabbable mode is enabled.
     *
     * @param event - The keyboard event
     */
    onKeydown(event: KeyboardEvent) {
        // Let the browser/OS handle any modifier-key shortcut (e.g. Cmd/Ctrl+2 to
        // switch browser tabs) instead of swallowing it while a list item is focused.
        if (event.metaKey || event.ctrlKey || event.altKey) {
            return;
        }

        // Don't prevent default for Escape key - let it bubble up to close overlays
        if (event.key === 'Escape') {
            return;
        }

        // Let Tab move focus normally instead of trapping it on the list item.
        if (event.key === 'Tab') {
            return;
        }

        // Handle Enter key for tabbable mode
        if (this.tabbable() && event.key === 'Enter') {
            this.#handleTabbableEnterKey(event);
            return;
        }

        // Handle Enter key for non-tabbable mode
        if (event.key === 'Enter') {
            // Check if an anchor is focused - if so, don't prevent default to allow navigation
            const focusedElement = document.activeElement as HTMLElement;
            if (focusedElement && this.#isAnchorElement(focusedElement)) {
                // Let the browser handle the anchor click
                return;
            }

            event.preventDefault();
            this.#handleEnterKeySelection();
            return;
        }

        // Only prevent default for the arrow keys we actually handle, so every other
        // key (type-ahead, browser shortcuts, etc.) keeps its native behaviour.
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.setNextFocussed();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.setPrevFocussed();
        }
    }

    /**
     * Handles Enter key press in tabbable mode.
     * @param event - The keyboard event
     */
    #handleTabbableEnterKey(event: KeyboardEvent) {
        const target = event.target as HTMLElement;

        // If an anchor is focused, let the browser handle the navigation
        if (this.#isAnchorElement(target)) {
            return; // Browser handles link navigation
        }

        // If a list item (without link) is focused, trigger its click
        if (this.#isListItemElement(target)) {
            event.preventDefault();
            target.click();
            return;
        }

        // Fall through to prevent default if needed
        event.preventDefault();
    }

    /**
     * Handles Enter key selection for non-tabbable mode.
     */
    #handleEnterKeySelection() {
        if (this.itemQueryList === undefined) {
            return;
        }

        const itemsArray = this.itemQueryList.toArray();
        const currentItem = itemsArray[this.dsListFocusIndex];
        const currentValue = currentItem.value();

        // Check if an anchor element is currently focused (for keyboard navigation on links)
        const focusedElement = document.activeElement as HTMLElement;
        if (focusedElement && this.#isAnchorElement(focusedElement)) {
            // Trigger click on the anchor to navigate
            focusedElement.click();
            return;
        }

        // Trigger a click event on the focused item to simulate user click
        currentItem.elRef.nativeElement.click();

        // Also emit selection change for backward compatibility
        if (currentValue) {
            this.#emitSelectionChange(currentValue);
        }
    }

    /**
     * Emits selection change event based on selection mode.
     * @param value - The value to emit
     */
    #emitSelectionChange(value: string) {
        if (this.multiple()) {
            this.#handleMultiSelection(value);
        } else {
            this.selectionChange.emit(value);
        }
    }

    /**
     * Checks if the target element is an anchor element.
     * @param target - The HTML element to check
     */
    #isAnchorElement(target: HTMLElement): boolean {
        return target && target.tagName.toLowerCase() === 'a';
    }

    /**
     * Checks if the target element is a list item element.
     * @param target - The HTML element to check
     */
    #isListItemElement(target: HTMLElement): boolean {
        return target && target.tagName.toLowerCase() === 'ds-list-item';
    }

    // ============================================================================
    // Public Methods
    // ============================================================================

    /**
     * Moves focus to the next non-disabled item in the list.
     * Implements circular navigation (wraps to beginning when reaching the end).
     *
     * If all items are disabled, logs a warning and returns without changing focus.
     */
    setNextFocussed() {
        if (!this.itemQueryList) return;

        const itemCount = this.itemQueryList.length;
        if (itemCount === 0) return;

        const itemsArray = this.itemQueryList.toArray();
        let nextIndex = this.dsListFocusIndex;
        let attempts = 0;

        // Find next non-disabled item (with circular navigation)
        do {
            nextIndex = nextIndex >= itemCount - 1 ? 0 : nextIndex + 1;
            attempts++;
            // Prevent infinite loop if all items are disabled
            if (attempts >= itemCount) {
                console.warn('⚠️ All items are disabled, cannot navigate');
                return;
            }
        } while (itemsArray[nextIndex]?.disabled);

        this.dsListFocusIndex = nextIndex;
        this.focusItem(this.dsListFocusIndex);
    }

    /**
     * Moves focus to the previous non-disabled item in the list.
     * Implements circular navigation (wraps to end when reaching the beginning).
     *
     * If all items are disabled, logs a warning and returns without changing focus.
     */
    setPrevFocussed() {
        if (!this.itemQueryList) return;

        const itemCount = this.itemQueryList.length;
        if (itemCount === 0) return;

        const itemsArray = this.itemQueryList.toArray();
        let prevIndex = this.dsListFocusIndex;
        let attempts = 0;

        // Find previous non-disabled item (with circular navigation)
        do {
            prevIndex = prevIndex <= 0 ? itemCount - 1 : prevIndex - 1;
            attempts++;
            // Prevent infinite loop if all items are disabled
            if (attempts >= itemCount) {
                console.warn('⚠️ All items are disabled, cannot navigate');
                return;
            }
        } while (itemsArray[prevIndex]?.disabled);

        this.dsListFocusIndex = prevIndex;
        this.focusItem(this.dsListFocusIndex);
    }

    /**
     * Focuses the list item at the specified index.
     * If the list has only one item, always focuses the first item (index 0).
     *
     * @param queryIndex - The index of the item to focus
     */
    focusItem(queryIndex: number) {
        if (this.itemQueryList !== undefined) {
            const itemsArray = this.itemQueryList.toArray();
            const queryIndexCheck = itemsArray.length > 1 ? queryIndex : 0;
            itemsArray[queryIndexCheck]?.elRef.nativeElement.focus();
        }
    }

    // ============================================================================
    // Private Methods
    // ============================================================================

    /**
     * Populates the itemQueryList with the appropriate list items.
     *
     * Priority order:
     * 1. dropdownItems (content children) - for dynamic overlays/context menus
     * 2. options (input) - for programmatic list items
     * 3. listItems (view children) - for template-defined list items
     */
    #populateListItems() {
        // Priority order: dropdownItems (content children) > options (input) > listItems (view children)
        if (this.dropdownItems && this.dropdownItems.length > 0) {
            this.itemQueryList = this.dropdownItems;
        } else if (this.options()) {
            const opts = this.options();
            if (opts && opts.length > 0) {
                this.itemQueryList = opts;
            }
        } else if (this.listItems?.length > 0) {
            this.itemQueryList = this.listItems;
        }
    }

    /**
     * Handles multi-selection toggle for a list item.
     *
     * When a value is already selected, it removes it from the selection.
     * When a value is not selected, it adds it to the selection.
     *
     * @param selectedValue - The value of the list item being toggled
     */
    #handleMultiSelection(selectedValue: string) {
        const hasSelectedValue = this.#selectedItems()?.includes(selectedValue);

        if (hasSelectedValue) {
            const newSelectedItems = this.#selectedItems().filter((item) => item !== selectedValue);
            this.#selectedItems.set(newSelectedItems);
        } else {
            this.#selectedItems.set([...this.#selectedItems(), selectedValue]);
        }
    }

    /**
     * This method initializes the selection change listener for the list items.
     *
     * It listens to the selectedChange event emitted by the list items and updates the selected items accordingly.
     *
     * Multiple Selection:
     * If multiple selection is enabled, it toggles the selected property of the list items based on the selected items.
     *
     * Single Selection:
     * A single selection is handled by setting the selected property of the list items based on the selected item. It also emits the selected item to the parent component.
     */
    #initSelectionChangeListener() {
        this.itemQueryList?.forEach((item: DsListItemComponent) => {
            if (this.multiple()) {
                // Subscribe to output and track for cleanup
                const subscription = item.selectedChange.subscribe((selectedValue: string) => {
                    this.#handleMultiSelection(selectedValue);
                });
                this.#subscriptions.push(subscription);
            } else {
                // Subscribe to output and track for cleanup
                const subscription = item.selectedChange.subscribe((selectedValue: string) => {
                    this.selectionChange.emit(selectedValue);
                });
                this.#subscriptions.push(subscription);
            }
        });
    }

    /**
     * Initializes the search term listener and triggers initial highlighting if a search term exists.
     */
    #initSearchListener() {
        if (this.searchTerm() !== undefined) {
            this.#highlightSearchTerm(this.searchTerm());
        }
    }

    /**
     * Highlights the search term within list item titles.
     *
     * Wraps matching text in span elements with a 'highlight' class for visual emphasis.
     * If the term is empty or null, restores the original text content.
     *
     * @param term - The search term to highlight (case-insensitive)
     */
    #highlightSearchTerm(term: string | undefined | null) {
        // Query all child elements with the 'ds-list-item-title' attribute
        const childElements = this.#elRef.nativeElement.querySelectorAll(
            'ds-list-option [ds-list-option-title]'
        ) as NodeListOf<HTMLElement>;

        childElements?.forEach((element: HTMLElement) => {
            // Store the original text content
            const originalTextContent = element.textContent || '';
            if (term && term.length > 0) {
                const parts = originalTextContent.split(new RegExp(`(${term})`, 'gi'));

                // Remove the existing text content.
                element.textContent = '';

                // Add new text content with searchTerm highlighted.
                parts.forEach((part: string) => {
                    const span = this.#renderer.createElement('span');
                    const text = this.#renderer.createText(part);

                    // If the part is the searchTerm, add the 'highlight' class.
                    if (part.toLowerCase() === term.toLowerCase()) {
                        this.#renderer.addClass(span, 'highlight');
                    }

                    this.#renderer.appendChild(span, text);
                    this.#renderer.appendChild(element, span);
                });
            } else {
                // If the searchTerm is removed, restore the original text content
                element.textContent = originalTextContent;
            }
        });
    }

    /**
     * Toggles selection state for list items in single-selection mode.
     *
     * Subscribes to selection changes from list items and updates the internal selected items signal.
     * This method is primarily used in conjunction with the mutation observer.
     */
    #toggleSelection() {
        this.listItems?.forEach((item: DsListItemComponent) => {
            // Subscribe to output and track for cleanup
            const subscription = item.selectedChange.subscribe((selectedValue: string) => {
                const itemValue = item.value();
                if (itemValue) {
                    // Update internal signal instead of trying to mutate input signal
                    this.#selectedItems.set([selectedValue]);
                }
            });
            this.#subscriptions.push(subscription);
        });
    }

    /**
     * Initializes a MutationObserver to watch for DOM changes in the list component.
     *
     * Observes:
     * - Child list changes: Triggers selection toggle when items are added/removed
     * - Size attribute changes: Updates size class on all list items
     */
    #initMutationObserver() {
        this.#observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    this.#toggleSelection();
                }

                if (mutation.type === 'attributes' && mutation.attributeName === 'size') {
                    const targetElement = mutation.target as Element;
                    const newSize = targetElement.getAttribute('size') as 'sm' | 'md' | 'lg' | null;
                    if (newSize) {
                        this.itemQueryList?.forEach((item: DsListItemComponent) => {
                            item.sizeClass = newSize;
                        });
                    }
                }
            }
        });

        // Select the node that will be observed for mutations
        const targetNode = this.#elRef.nativeElement;

        // Options for the observer (which mutations to observe)
        const config = { attributes: true, childList: true, subtree: false };

        // Check if targetNode is defined before observing
        if (targetNode) {
            // Start observing the target node for configured mutations
            this.#observer.observe(targetNode, config);
        } else {
            console.error('Target node not defined');
        }
    }

    /**
     * Checks if a given element is inside this list component.
     *
     * @param element - The element to check
     * @returns True if the element is inside the ds-list component, false otherwise
     */
    #elementIsInsideComponent(element: HTMLElement): boolean {
        return !!element.closest('ds-list');
    }
}

/**
 * A constant array containing all list-related components and directives for convenient importing.
 *
 * Use this when you need to import all list functionality in a module or component.
 *
 * @example
 * ```typescript
 * imports: [...DsListImports]
 * ```
 */
export const DsListImports = [
    DsListComponent,
    DsListItemComponent,
    DsListItemDescriptionDirective,
    DsListItemSuffixDirective,
    DsListItemPrefixDirective,
    DsListItemTitleDirective,
    DsListItemDisabledDirective,
    DsListItemDividerDirective,
] as const;
